import { Sprite } from "@pixi/sprite";
import { ButtonWithScaleAndPosYAnimation } from "../../common/components/ButtonWithScaleAndPosYAnimation";
import { IDestroyOptions, Text, TextStyle } from "pixi.js";
import { VariableSaver } from "by/gamefactory/tools/variableSaver/VariableSaver";
import { IPlayerModel } from "../../common/models/IPlayerModel";
import { IGuestModel } from "../../common/models/IGuestModel";
import { nameof } from "../../../../shared/bindable-data";
import { GameFont } from "../../common/GameFont";
import { GameResourcePackage } from "../resources/GameResourcePackage";
import { CommonResourcesPackage } from "../../common/resources/CommonResourcesPackage";

export class MyBuildingsButton extends ButtonWithScaleAndPosYAnimation {

    private readonly gameResources: GameResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly playerModel: IPlayerModel;
    private readonly guestModel: IGuestModel;
    private readonly variableSaver: VariableSaver;
    private newBuildingsSprite: Sprite;
    private hardHat: Sprite;
    private houseIcon: Sprite;
    private light: Sprite;
    private newBuildingsText: Text;
    private tutorialShowed: boolean;

    public get tutorialAvailable(): boolean {
        return this.tutorialShowed;
    }

    public set tutorialAvailable(value: boolean) {
        this.tutorialShowed = value;
    }

    public constructor(
        gameResources: GameResourcePackage,
        commonResources: CommonResourcesPackage,
        playerModel: IPlayerModel,
        guestModel: IGuestModel,
        variableSaver: VariableSaver
    ) {
        super(gameResources.assets_views_game_ui_building_button_my_building_button_png);
        this.gameResources = gameResources;
        this.commonResources = commonResources;
        this.playerModel = playerModel;
        this.guestModel = guestModel;
        this.variableSaver = variableSaver;
        this.build();
        this.playerModel.propertyChanged.add(this.onPlayerModelPropertyChanged, this);
        this.once("added", this.show);
    }

    private async onPlayerModelPropertyChanged(model: IPlayerModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "buildings"):
                if (!this.tutorialShowed) {
                    this.updateNewBuildingsText();
                }
                break;
            case nameof(model, "hasBuildingFixes"):
                this.updateFixIcons(model.hasBuildingFixes.value);
                break;
        }
    }

    private build(): void {
        this.houseIcon = new Sprite(this.gameResources.assets_views_game_ui_building_button_MB_icon_png);
        this.houseIcon.position.set((this.defaultView.width - this.houseIcon.width) * 0.5 + 2, (this.defaultView.height - this.houseIcon.height) * 0.5 - 3);
        this.defaultView.addChild(this.houseIcon);
        this.newBuildingsSprite = new Sprite(this.commonResources.assets_common_ui_indicator_point_png);
        this.newBuildingsSprite.position.set(this.defaultView.width - this.newBuildingsSprite.width * 0.5 - 15, -this.newBuildingsSprite.height * 0.5 + 5);
        this.newBuildingsText = new Text("1", new BuildingsCountTextStyle());
        this.newBuildingsText.position.set((this.newBuildingsSprite.width - this.newBuildingsText.width) * 0.5, (this.newBuildingsSprite.height - this.newBuildingsText.height) * 0.5);
        this.newBuildingsSprite.addChild(this.newBuildingsText);
        this.defaultView.addChild(this.newBuildingsSprite);
        this.hardHat = new Sprite(this.gameResources.assets_views_game_ui_building_button_hard_hat_png);
        this.hardHat.position.set(this.defaultView.width - this.hardHat.width + 15, -this.hardHat.height * 0.5 + 5);
        this.addChild(this.hardHat);
        this.light = new Sprite(this.gameResources.assets_views_game_ui_marketplace_button_light_white_png);
        this.light.position.set((this.defaultView.width - this.light.width) * 0.5, this.defaultView.height - this.light.height * 0.5 + 16);
        this.addChild(this.light);
    }

    private show(): void {
        this.onPlayerModelPropertyChanged(this.playerModel, "buildings");
        this.onPlayerModelPropertyChanged(this.playerModel, "hasBuildingFixes");
    }

    public unLockButtonWithTutorial(): void {
        this.visible = true;
        this.newBuildingsText.text = " ";
        this.newBuildingsSprite.visible = true;
        this.light.texture = this.gameResources.assets_views_game_ui_building_button_light_red_png;
    }

    public updateNewBuildingsText(): void {
        let newBuildingsCount = 0;
        this.playerModel.buildings.value.forEach((item) => {
            if (!this.variableSaver.exist(`${this.guestModel.id.value},${item.id.value}`)) {
                newBuildingsCount++;
            }
        });
        this.newBuildingsText.text = newBuildingsCount.toString();
        this.newBuildingsSprite.visible = newBuildingsCount > 0;
        this.light.texture = this.playerModel.hasBuildingFixes.value || newBuildingsCount > 0 ?
            this.gameResources.assets_views_game_ui_building_button_light_red_png :
            this.gameResources.assets_views_game_ui_marketplace_button_light_white_png;
    }

    public updateFixIcons(hasBuildingFixes: boolean): void {
        this.hardHat.visible = hasBuildingFixes;
        this.houseIcon.tint = hasBuildingFixes ? 0xFC414B : 0xFFFFFF;
        this.light.texture = hasBuildingFixes || this.newBuildingsSprite.visible ? this.gameResources.assets_views_game_ui_building_button_light_red_png : this.gameResources.assets_views_game_ui_marketplace_button_light_white_png;
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        super.destroy(options);
    }
}

class BuildingsCountTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 36
        }));
    }
}
